import { createContext, FC, useCallback, useState } from "react";
import useCallToService from "../../../hooks/useCallToService";
import { IAgent } from "../../../models/entities/Agent";

type IAgentEditServiceField = {
  endpoint: string;
  games: string[];
  agentType: string;
};

type IAgentEditServiceProps = {
  id: string;
  data: IAgent;
  fields: IAgentEditServiceField;
  isProcessing: boolean;
  error: string;
  setId: Function;
  setFields: Function;
  saveFields: Function;
  fetch: Function;
};

const defaultContext: IAgentEditServiceProps = {
  id: "",
  data: {
    username: "",
    endpoint: "",
    secret: "",
    apiKey: "",
    apiKeyEncoded: "",
    games: [],
    prefix: "",
    code: "",
    agentType: "",
    createdBy: "",
    locked: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  fields: {
    endpoint: "",
    games: [],
    agentType: "",
  },
  isProcessing: false,
  error: "",
  setId: () => { },
  setFields: () => { },
  saveFields: () => { },
  fetch: () => { },
};
const createGetServiceEndpoint = (id: string, manualId?: string) =>
  `${process.env.REACT_APP_BACKEND_ENDPOINT}/api/v1/agent/get/${manualId || id}`;
const createUpdateServiceEndpoint = (id: string, manualId?: string) =>
  `${process.env.REACT_APP_BACKEND_ENDPOINT}/api/v1/agent/update/${manualId || id}`;
export const AgentEditServiceContext = createContext(defaultContext);
export const AgentEditServiceContextProvider: FC<any> = ({ children }) => {
  const [id, setId] = useState(defaultContext.id);
  const [data, setData] = useState<IAgent>(defaultContext.data);
  const [fields, setLocalFields] = useState(defaultContext.fields);
  const [isProcessing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [callToService] = useCallToService();

  const fetch = useCallback(
    async (manualId?: string) => {
      setError("");
      setProcessing(true);
      try {
        const response = await callToService(
          createGetServiceEndpoint(id, manualId),
          "POST",
          {}
        );

        if (response.data) {
          setData(response.data);
          setLocalFields({
            endpoint: response.data.endpoint,
            games: response.data.games,
            agentType: response.data.agentType,
          });
        } else if (response.statusCode !== 1000) {
          setError(response.statusMessage);
        }
        setProcessing(false);
      } catch (error) {
        setError((error as Error).message);
        setProcessing(false);
      }
    },
    [callToService, id]
  );

  const saveFields = useCallback(
    async (callback: Function, manualId?: string) => {
      setProcessing(true);
      try {
        const response = await callToService(
          createUpdateServiceEndpoint(id, manualId),
          "PUT",
          fields,
        );

        if (response.data) {
          setData(response.data);
          setLocalFields({
            endpoint: response.data.endpoint,
            games: response.data.games,
            agentType: response.data.agentType,
          });
          callback(response.data);
        } else if (response.statusCode !== 1000) {
          setError(response.statusMessage);
        }
        setProcessing(false);
      } catch (error) {
        setError((error as Error).message);
        setProcessing(false);
      }
    },
    [callToService, fields, id]
  );

  const setFields = (field: string, value: any) => {
    setLocalFields({ ...fields, [field]: value });
  };

  return (
    <AgentEditServiceContext.Provider
      value={{
        id,
        data,
        fields,
        isProcessing,
        error,
        setId,
        setFields,
        saveFields,
        fetch,
      }}>
      {children}
    </AgentEditServiceContext.Provider>
  );
};
